/**
 * Zoe answer feedback (asker's thumbs up/down) and the Truth Layer question
 * log (org-wide, admin / `zoe-question-log` permission only).
 */
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/apiClient';
import type {
  AnswerFeedbackNote,
  AnswerFeedbackRating,
  AnswerFeedbackReason,
  AnswerRating,
  QuestionLogResponse,
} from '../types/zoeFeedback';

const OPS_AGENT = `${API_ENDPOINTS.base || ''}/api/operations-agent`;

async function readError(res: Response, fallback: string): Promise<Error> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  return new Error(body?.error || `${fallback} (${res.status})`);
}

export async function submitAnswerFeedback(input: {
  conversationId: string;
  runId: string;
  rating: AnswerRating;
  reasons?: AnswerFeedbackReason[];
  comment?: string;
  answerSeen?: string;
}): Promise<AnswerFeedbackRating> {
  const res = await authenticatedFetch(
    `${OPS_AGENT}/conversations/${encodeURIComponent(input.conversationId)}/feedback`,
    {
      method: 'POST',
      body: JSON.stringify({
        run_id: input.runId,
        rating: input.rating,
        reasons: input.reasons ?? [],
        ...(input.comment ? { comment: input.comment } : {}),
        ...(input.answerSeen ? { answer_seen: input.answerSeen } : {}),
      }),
    },
  );
  if (!res.ok) throw await readError(res, 'Could not save feedback');
  const json = (await res.json()) as { feedback: AnswerFeedbackRating };
  return json.feedback;
}

export async function clearAnswerFeedback(conversationId: string, runId: string): Promise<void> {
  const res = await authenticatedFetch(
    `${OPS_AGENT}/conversations/${encodeURIComponent(conversationId)}/feedback/${encodeURIComponent(runId)}`,
    { method: 'DELETE' },
  );
  if (!res.ok) throw await readError(res, 'Could not clear feedback');
}

export async function fetchQuestionLog(params: { limit?: number; since?: string; until?: string } = {}): Promise<QuestionLogResponse> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.since) qs.set('since', params.since);
  if (params.until) qs.set('until', params.until);
  const suffix = qs.toString() ? `?${qs}` : '';
  // Reads every conversation doc in the org — allow for a slow cold object store.
  const res = await authenticatedFetch(`${OPS_AGENT}/question-log${suffix}`, {}, 120_000);
  if (!res.ok) throw await readError(res, 'Could not load the question log');
  return (await res.json()) as QuestionLogResponse;
}

export async function addQuestionLogNote(input: {
  userId: string;
  conversationId: string;
  runId: string;
  /** Lets the server find an errored question's record without a scan. */
  askedAt?: string;
  text: string;
}): Promise<AnswerFeedbackNote[]> {
  const res = await authenticatedFetch(`${OPS_AGENT}/question-log/notes`, {
    method: 'POST',
    body: JSON.stringify({
      user_id: input.userId,
      conversation_id: input.conversationId,
      run_id: input.runId,
      ...(input.askedAt ? { asked_at: input.askedAt } : {}),
      text: input.text,
    }),
  });
  if (!res.ok) throw await readError(res, 'Could not save note');
  const json = (await res.json()) as { notes: AnswerFeedbackNote[] };
  return json.notes;
}
